import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/latest'
import * as date from '@owl-nest/date'
import * as env from '@owl-nest/config'
import { t, tn, tp } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as services from '@owl-nest/services'

type RewardCardProps = {
  className?: string
  disabled?: boolean
  fullWidth?: boolean
  onSelect: plume.RewardCardProps['onSelect']
  reward: models.reward.Reward
  selected?: boolean
  showPlaceholderImage?: boolean
  showRewardTag?: boolean
  stockOffsets?: Record<string, number>
  usePortalForModal?: boolean
}

export function RewardCard({
  className,
  disabled,
  fullWidth,
  onSelect,
  reward,
  selected,
  showPlaceholderImage = false,
  showRewardTag = true,
  stockOffsets,
  usePortalForModal,
}: RewardCardProps): React.ReactElement {
  const project = reward.project
  const rewardModel = reward
  const deliveryDate = reward.shippings?.date_delivery
  const isSelectedFromStorage = isSelectedRewardFromStorage(reward)
  const userConfig = env.useUserEnv()
  const ConversionIcon = getCurrencyIcon(userConfig.currency)

  const awareStockAvailable = reward.aware_stock_available ?? undefined
  const selectedQuantity = isSelectedFromStorage?.quantity ?? 0
  const awareAvailable = awareStockAvailable ? awareStockAvailable - selectedQuantity : awareStockAvailable

  if (project === undefined) {
    return <></>
  }

  const formattedReward = {
    id: reward.id.toString(),
    description: { __html: models.reward.description(rewardModel) ?? '' },
    img: models.reward.image(rewardModel),
    isCombined: reward.is_combined,
    isFeatured: reward.is_featured,
    isHidden: reward.is_hidden,
    ...(reward.options &&
      reward.options.length > 0 && {
        options: reward.options.map((option) => {
          return {
            // @ts-expect-error FIXME: why does it infer the wrong type?
            description: models.option.description(option),
            id: String(option.id),
            // @ts-expect-error FIXME: why does it infer the wrong type?
            name: models.option.name(option) ?? '',
            values: option.values.map((value) => {
              const stockOffset = (stockOffsets && stockOffsets[value.id]) || 0
              return {
                additionalCost: value.additional_cost,
                id: value.id?.toString() ?? value.identifier,
                imageURL: value.image
                  ? models.image.getUrl(
                      models.i18n.getWithLang(value.image, userConfig.lang, project.lang)?.versions,
                      'large',
                    )
                  : undefined,
                // @ts-expect-error FIXME: why does it infer the wrong type?
                title: models.option.valueDescription(value, option) ?? '',
                stock: {
                  available: getStockAvailable(value, stockOffset),
                },
              }
            }),
          }
        }),
      }),
    stock: {
      available: awareAvailable,
    },
    taxDeductible: reward.tax_deductible,
    title: models.reward.title(rewardModel) ?? '',
    ...(reward.variants && reward.variants.length > 0
      ? {
          variants: reward.variants
            .filter((variant) => Boolean(variant))
            .map((variant) => {
              const stockOffset = (stockOffsets && stockOffsets[variant.id]) || 0
              return {
                additionalCost: variant.additional_cost,
                id: variant.id?.toString(),
                imageURL: variant.image
                  ? models.image.getUrl(models.i18n.getWithLang(variant.image, project.lang)?.versions, 'large')
                  : undefined,
                price: variant.price ?? 0,
                stock: {
                  available: getAwareStockAvailable(variant, stockOffset),
                },
                title: variant.parent?.project ? models.variant.description(variant) : '',
              }
            }),
        }
      : {}),
  }

  return (
    <plume.RewardCard
      className={className}
      currency={project.currency}
      deliveryDateTag={
        deliveryDate ? (
          <plume.rewardTag.DeliveryDateTag>
            {/* Format the delivery date to 'MMMM yyyy' and capitalize the first letter */}
            {date
              .format(date.createDateWithTimezone(new Date(deliveryDate), 'Etc/UTC'), 'MMMM yyyy')
              .replace(/^\w/, (c) => c.toUpperCase())}
          </plume.rewardTag.DeliveryDateTag>
        ) : undefined
      }
      disabled={disabled}
      fullWidth={fullWidth}
      isCombined={reward.is_combined}
      isMultiVariationsEnabled={project.multi_variations_enabled}
      isProjectFinished={models.project.isFinished(project)}
      locale={userConfig.lang || env.DEFAULT_LOCALE}
      onSelect={onSelect}
      onSelectOptions={async (
        rewardId: string,
        valueIds: string[],
      ): Promise<{
        available: boolean
        id: string
        imageUrl?: string
        stock: {
          available: number | null
          total: number | null
        }
      }> => {
        if (project.multi_variations_enabled && valueIds.length >= 1) {
          const response = await services.project.rewardVariants(rewardId, valueIds, !models.project.isOnline(project))
          const variantResponse = response.next((success) => success.variants[0])
          if (variantResponse.isRight()) {
            const variant = variantResponse.unwrap()
            const stockOffset = (stockOffsets && stockOffsets[variant.id]) || 0
            return {
              available: variant.available,
              id: String(variant.id),
              imageUrl: variant.image
                ? models.image.getUrl(
                    models.i18n.getWithLang(variant.image, userConfig.lang, project.lang)?.versions,
                    'large',
                  )
                : undefined,
              stock: {
                available: getAwareStockAvailable(variant, stockOffset),
                total: variant.aware_stock,
              },
            }
          }
        }

        // Cas des projets sans multivar ou avec multivar mais une seule liste de variations
        const variant = reward.variants.find((variant) => variant.id === Number(valueIds[0]))
        return {
          available: Boolean(variant?.available),
          id: variant ? String(variant.id) : '',
          stock: {
            available: variant ? variant.aware_stock_available : null,
            total: variant ? variant.aware_stock : null,
          },
        }
      }}
      reward={formattedReward}
      mainImage={
        reward.project.multi_variations_enabled ? (
          <plume.RewardCarousel
            reward={formattedReward}
            locale={window.UFE.locale}
            currency={reward.project.currency}
          />
        ) : undefined
      }
      selected={selected}
      showPlaceholderImage={showPlaceholderImage}
      stockTag={
        reward.orders_count && reward.orders_count > 0 ? (
          <plume.rewardTag.StockTag>
            {tn('%(ordersCount)d contribution', '%(ordersCount)d contributions', reward.orders_count, {
              ordersCount: Number(reward.orders_count),
            })}
          </plume.rewardTag.StockTag>
        ) : undefined
      }
      tag={
        showRewardTag &&
        (reward.is_featured ? (
          <plume.rewardTag.FeaturedTag>{t('Top reward')}</plume.rewardTag.FeaturedTag>
        ) : reward.is_hidden ? (
          <plume.rewardTag.HiddenTag>{t('Secret reward')}</plume.rewardTag.HiddenTag>
        ) : undefined)
      }
      targetCurrency={userConfig.currency}
      translations={{
        availableVariantStock: (stockAvailable) =>
          tn('%(num)d available', '%(num)d available', stockAvailable, {
            num: Number(stockAvailable),
          }),
        cta: t('Choose'),
        disabledCTA: t('This button will be active when your page is online.'),
        options: getOptionsTranslation(reward),
        ...(userConfig.currency !== project.currency && env.CURRENCIES_RATES
          ? {
              convertedCurrency: (
                <Conversion>
                  <ConversionIcon size={14} />
                  ~
                  <plume.LocaleMoneyDisplayer
                    amount={reward.price}
                    currency={project.currency}
                    targetCurrency={userConfig.currency}
                    localeFormat={userConfig.lang ?? env.DEFAULT_LOCALE}
                    rates={env.CURRENCIES_RATES}
                  />
                </Conversion>
              ),
            }
          : {}),
        remove: t('Remove'),
        finished: tp('Project ended', 'reward based'),
        soldOut: tp('Sold out', 'Variant modal'),
        soonSoldOut: t('Soon sold out'),
        variantModalTitle: tn(
          'Select your option',
          'Select your options',
          project.multi_variations_enabled && reward.options ? reward.options.length : 1,
        ),
        variantModalCta: t('Confirm'),
        price: `${t('For %(price)s', {
          price: plume.accounting.localeMoney(reward.price, {
            currency: project.currency,
            lang: userConfig.lang ?? env.DEFAULT_LOCALE,
          }),
        })}`,
        ...(reward.aware_stock_available && {
          stockAvailable: tn('%(num)d available', '%(num)d available', reward.aware_stock_available, {
            num: Number(reward.aware_stock_available - (isSelectedFromStorage ? isSelectedFromStorage.quantity : 0)),
          }),
        }),
        ...(reward.tax_deductible && {
          taxDeduction: t('%(price)s after tax reduction', {
            price: plume.accounting.localeMoney(reward.tax_deductible_price, {
              currency: project.currency,
              lang: userConfig.lang ?? env.DEFAULT_LOCALE,
            }),
          }),
        }),
        ...(isSelectedFromStorage && {
          quantitySelected: tn(
            'You have already added %(quantity)d quantity',
            'You have already added %(quantity)d quantities',
            isSelectedFromStorage.quantity,
            {
              quantity: isSelectedFromStorage.quantity,
            },
          ),
        }),
      }}
      usePortalForModal={usePortalForModal}
    />
  )
}

function getAwareStockAvailable(variant: api.Variant, stockOffset: number) {
  return variant.aware_stock_available ? variant.aware_stock_available - stockOffset : variant.aware_stock_available
}

function getStockAvailable(variant: api.RewardOptionValue, stockOffset: number) {
  return variant.stock_available ? variant.stock_available - stockOffset : variant.stock_available
}

function getOptionsTranslation(reward: models.reward.Reward) {
  if (reward.options && reward.options.length === 1) {
    const lastOption = models.option.name(reward.options[0])

    return t('Choice of %(lastOption)s', {
      lastOption,
    })
  } else if (reward.options && reward.options.length > 0) {
    const options = reward.options
      .slice(0, -1)
      .map((option) => models.option.name(option as models.option.Option))
      .join(', ')
    const lastOption = models.option.name(reward.options[reward.options.length - 1])

    return t('Choice of %(options)s and %(lastOption)s', {
      options,
      lastOption,
    })
  }

  if (reward.variants && reward.variants.length > 0) {
    return t('%(numberOfOptions)s options available', {
      numberOfOptions: reward.variants.length,
    })
  }

  return undefined
}

function isSelectedRewardFromStorage(reward: models.reward.Reward) {
  const project = reward.project

  if (project === undefined) {
    return undefined
  }

  const store = services.cart.getCartFromLocalStorage(project.id)

  if (store === undefined) {
    return undefined
  }

  const rewardSelected = store?.products?.find((product) => {
    return product.id === reward.id
  })

  let variantSelected

  if (reward.variants && reward.variants.length > 0 && store?.products) {
    // Several variants of a same reward might be in the store, we then look for the
    // total selected quantity for this reward by suming the selected quantity of each of its variants
    const selectedVariantsQuantitiesSum = store?.products?.reduce((totalQuantity, product) => {
      return reward.variants.find((variant) => variant.id === product.id)
        ? totalQuantity + product.quantity
        : totalQuantity
    }, 0)

    if (selectedVariantsQuantitiesSum > 0) {
      variantSelected = {
        id: reward.id,
        quantity: selectedVariantsQuantitiesSum,
      }
    }
  }

  return rewardSelected || variantSelected
}

function getCurrencyIcon(currency: string | undefined): React.ComponentType<any> {
  let iconName = 'Dolls'

  if (currency === 'EUR') {
    iconName = 'Eur'
  } else if (currency === 'CHF') {
    iconName = 'Chf'
  } else if (currency === 'DKK' || currency === 'NOK' || currency === 'SEK') {
    iconName = 'Kr'
  } else if (currency === 'GBP') {
    iconName = 'Pound'
  }

  return (plume.glyphs as any)['currencies'][`${iconName}`]
}

const Conversion = styled.div`
  align-items: center;
  display: flex;

  ${plume.glyphs.Icon} {
    position: relative;
    top: 2px;
    margin-right: 5px;
  }
`
